import { CircularProgress } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { FC, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { auctionApi, Bid, BID_AMOUNTS } from 'src/api/auctionApi';

import useAppSelector from 'src/hooks/Redux/useAppSelector';
import { currentUser } from 'src/redux/slices/userSlice';
import { AuctionUtils } from '../../AuctionUtils';

import { useAuctionCloseCountDown } from 'src/hooks/Auction/useAuctionCloseCountDown';
import { BidResult } from '../../BidInfo/BidResultEnum';
import styles from './TotalDue.module.scss';

interface TotalDueProps {
  price: number | undefined;
  existingBid?: Bid | undefined | null;
  closeDate?: string;
  bidResult?: BidResult | null;
  onRefreshAuctionData: () => void;
}

export const TotalDue: FC<TotalDueProps> = ({
  price = 0,
  existingBid,
  closeDate,
  bidResult,
  onRefreshAuctionData,
}) => {
  const isAuctionClosed = useAuctionCloseCountDown(closeDate);

  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedBidAmount, setSelectedBidAmount] = useState<string>(
    price.toString()
  );

  const user = useAppSelector(currentUser);
  const { id: auctionId } = useParams();

  useEffect(() => {
    if (price) {
      setSelectedBidAmount(price.toString());
    }
  }, [price]);

  const handleBidAmountChange = (event: SelectChangeEvent) => {
    setSelectedBidAmount(event.target.value);
  };

  const handleBidAmountError = (errorMessage: string) => {
    // error message is based on backend src/features/auctions/bids.service.ts
    if (errorMessage.includes('User is already a member of the tribe')) {
      setError('You are already a member of this tribe.');
    } else if (
      errorMessage.includes('Bid is lower than lowest available bid')
    ) {
      setError(
        'The lowest bid amount just updated, please select a higher bid amount'
      );
    } else if (
      errorMessage.includes('Bid amount cannot be lower than existing amount')
    ) {
      setError(
        'You can only place a new bid if you are going to increase your existing bid amount'
      );
    } else {
      console.log(`bid error ${JSON.stringify(errorMessage)}`);
      setError('Something went wrong. Please try again later');
    }
  };

  const onMakeBid = async () => {
    setError(null);
    setLoading(true);

    if (!user || !auctionId) {
      setLoading(false);
      return;
    }

    const bidAmount = parseInt(selectedBidAmount, 10);

    try {
      const bidObject =
        existingBid && existingBid._id
          ? await auctionApi.updateBidAmount(existingBid._id, bidAmount)
          : await auctionApi.createBid(auctionId, bidAmount);

      const redirectUrl = bidObject.stripeSession?.redirectUrl!;
      if (!redirectUrl) {
        throw new Error('Unable to resolve redirectUrl');
      }

      onRefreshAuctionData();

      window.location.href = redirectUrl;
    } catch (error: any) {
      const errorMessage =
        error.message ??
        error.response?.data?.message ??
        'An error occurred while processing your bid.';

      handleBidAmountError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const bidAmountOptions = BID_AMOUNTS.filter((amount) => amount >= price).map(
    (amount) => ({
      value: amount.toString(),
      label: AuctionUtils.formatDollars(amount, 0),
    })
  );

  return (
    <div className={styles.container}>
      <div className={styles.bidAmountContainer}>
        <p className={styles.bidAmount}>Bid Amount</p>
        <div className={styles.bidAmountSelectorContainer}>
          <FormControl>
            <Select
              value={selectedBidAmount}
              onChange={handleBidAmountChange}
              displayEmpty
              variant="outlined"
              sx={{
                height: '32px',
                backgroundColor: 'transparent',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#ccc',
                  borderRadius: '4px',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#999',
                },
                '& .MuiSelect-select': {
                  paddingTop: '4px',
                  paddingBottom: '4px',
                  textAlign: 'center',
                },
              }}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 200,
                  },
                },
              }}
            >
              {bidAmountOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <span className={styles.bidAmount}>/ month</span>
        </div>
      </div>

      <div className={styles.cancelInfo}>
        <div className={styles.infoWrapper}>
          <div className={styles.cancelTitle}>
            Cancel any time and subscribe with confidence with
          </div>
          {/* <a
            href={CONSTANTS.ExternalUrl.TermsAndConditions}
            target="_blank"
            rel="noreferrer"
            className={styles.cancelDesc}
          > */}
          <p style={{ fontSize: '14px', paddingTop: '2px' }}>
            Dunbar148's Circle of Trust Guarantee
          </p>
          {/* </a> */}
        </div>
        <div className={styles.letter}>D</div>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      <button
        className={styles.btn}
        disabled={
          loading ||
          !user ||
          bidAmountOptions.length === 0 ||
          bidResult === BidResult.AUCTION_CLOSING ||
          isAuctionClosed
        }
        onClick={onMakeBid}
      >
        {loading ? (
          <CircularProgress size={24} style={{ color: 'white' }} />
        ) : existingBid?.is_winning_bid ? (
          <span className={styles.buttonText}>Increase Bid</span>
        ) : existingBid?.bid_amount_usd ? (
          <span className={styles.buttonText}>Place New Bid</span>
        ) : (
          <span className={styles.buttonText}>Place Bid</span>
        )}
      </button>
    </div>
  );
};
